import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Dialog } from '@headlessui/react'
import Button from '@atlaskit/button'
import FolderIcon from '@atlaskit/icon/glyph/folder'
import FolderFilledIcon from '@atlaskit/icon/glyph/folder-filled'
import DocumentsIcon from '@atlaskit/icon/glyph/documents'
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right'
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down'
import {
  moveScenarioToFolderAsync,
  selectFolderTree
} from '../../store/slices/folderSlice'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * MOVE SCENARIO MODAL
 *
 * Modal dialog for moving a scenario to a different folder.
 * Shows a folder tree picker.
 */
function MoveScenarioModal({
  isOpen,
  onClose,
  onMoveSuccess,
  scenario,
  currentFolderId
}) {
  const dispatch = useDispatch()
  const folderTree = useSelector(selectFolderTree)
  const { showError, showWarning } = useNotification()

  // Selected folder state
  const [selectedFolderId, setSelectedFolderId] = useState(null) // null = root/no folder
  const [expandedFolders, setExpandedFolders] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Toggle folder expand/collapse
  const handleToggleExpand = (folderId, e) => {
    e.stopPropagation()
    if (expandedFolders.includes(folderId)) {
      setExpandedFolders(expandedFolders.filter(id => id !== folderId))
    } else {
      setExpandedFolders([...expandedFolders, folderId])
    }
  }

  // Handle folder selection
  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(folderId)
  }

  // Handle form submission
  const handleSubmit = async () => {
    if (!scenario?.id) {
      return
    }

    // Don't allow moving to same folder
    if (selectedFolderId === currentFolderId) {
      showWarning('Cannot Move', 'Scenario is already in this folder')
      return
    }

    setIsSubmitting(true)

    try {
      await dispatch(moveScenarioToFolderAsync({
        testScenarioId: scenario.id,
        moveData: {
          folderId: selectedFolderId
        }
      })).unwrap()

      // Notify parent to refresh folder tree counts and scenario list
      onMoveSuccess?.()
      onClose()
    } catch (error) {
      console.error('Failed to move scenario:', error)
      const errorMessage = error?.message || 'Failed to move scenario. Please try again.'
      showError('Move Scenario Failed', errorMessage)
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    setSelectedFolderId(null)
    setExpandedFolders([])
    onClose()
  }

  // Render folder tree recursively
  const renderFolderTree = (folders, level = 0) => {
    return folders.map(folder => {
      const isExpanded = expandedFolders.includes(folder.id)
      const isSelected = selectedFolderId === folder.id
      const isCurrent = folder.id === currentFolderId
      const hasChildren = folder.children && folder.children.length > 0
      const leftPadding = 12 + (level * 20)

      return (
        <React.Fragment key={folder.id}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: `8px 12px 8px ${leftPadding}px`,
              borderRadius: '4px',
              cursor: isCurrent ? 'not-allowed' : 'pointer',
              backgroundColor: isSelected
                ? 'var(--ds-background-selected, #DEEBFF)'
                : 'transparent',
              opacity: isCurrent ? 0.5 : 1,
              marginBottom: '2px'
            }}
            onClick={() => !isCurrent && handleSelectFolder(folder.id)}
          >
            {/* Chevron */}
            <div
              style={{
                width: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: hasChildren ? 1 : 0
              }}
              onClick={(e) => hasChildren && handleToggleExpand(folder.id, e)}
            >
              {hasChildren && (
                isExpanded
                  ? <ChevronDownIcon label="Collapse" size="small" />
                  : <ChevronRightIcon label="Expand" size="small" />
              )}
            </div>

            {/* Folder Icon */}
            <div style={{ marginRight: '8px' }}>
              {isSelected ? (
                <FolderFilledIcon label="Folder" size="small" primaryColor="var(--ds-icon-selected, #0052CC)" />
              ) : (
                <FolderIcon label="Folder" size="small" />
              )}
            </div>

            {/* Folder Name */}
            <span style={{
              flex: 1,
              fontSize: '14px',
              fontWeight: isSelected ? 600 : 400,
              color: isSelected
                ? 'var(--ds-text-selected, #0052CC)'
                : 'var(--ds-text, #172B4D)'
            }}>
              {folder.name}
              {isCurrent && ' (current)'}
            </span>
          </div>

          {/* Children */}
          {isExpanded && hasChildren && renderFolderTree(folder.children, level + 1)}
        </React.Fragment>
      )
    })
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="headless-dialog-root">
      {/* Backdrop */}
      <div
        className="headless-dialog-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--ds-blanket)',
          zIndex: 1000
        }}
        aria-hidden="true"
      />

      {/* Modal Container - pointer-events:none prevents click detection, Panel has pointer-events:auto */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          pointerEvents: 'none'
        }}
      >
        <Dialog.Panel
          style={{
            backgroundColor: 'var(--ds-surface-overlay, #FFFFFF)',
            borderRadius: '8px',
            boxShadow: '0 8px 16px -4px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
            width: '400px',
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: 'auto'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid var(--ds-border, #DFE1E6)'
            }}
          >
            <Dialog.Title
              style={{
                fontSize: '20px',
                fontWeight: 500,
                color: 'var(--ds-text, #172B4D)',
                margin: 0
              }}
            >
              Move Scenario
            </Dialog.Title>
            <p style={{
              fontSize: '13px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              margin: '8px 0 0 0'
            }}>
              Select a destination folder for "{scenario?.scenarioName}"
            </p>
          </div>

          {/* Body - Folder Tree */}
          <div style={{
            flex: 1,
            overflow: 'auto',
            padding: '12px'
          }}>
            {/* Root / No Folder option */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 12px',
                borderRadius: '4px',
                cursor: currentFolderId === null ? 'not-allowed' : 'pointer',
                backgroundColor: selectedFolderId === null
                  ? 'var(--ds-background-selected, #DEEBFF)'
                  : 'transparent',
                opacity: currentFolderId === null ? 0.5 : 1,
                marginBottom: '4px'
              }}
              onClick={() => currentFolderId !== null && handleSelectFolder(null)}
            >
              <div style={{ width: '20px' }} />
              <DocumentsIcon
                label="Root"
                size="small"
                primaryColor={selectedFolderId === null
                  ? 'var(--ds-icon-selected, #0052CC)'
                  : 'var(--ds-icon, #6B778C)'}
                style={{ marginRight: '8px' }}
              />
              <span style={{
                fontSize: '14px',
                fontWeight: selectedFolderId === null ? 600 : 400,
                color: selectedFolderId === null
                  ? 'var(--ds-text-selected, #0052CC)'
                  : 'var(--ds-text, #172B4D)'
              }}>
                No Folder (Root)
                {currentFolderId === null && ' (current)'}
              </span>
            </div>

            {/* Divider */}
            <div style={{
              height: '1px',
              backgroundColor: 'var(--ds-border, #DFE1E6)',
              margin: '8px 0'
            }} />

            {/* Folder Tree */}
            {folderTree.length > 0 ? (
              renderFolderTree(folderTree)
            ) : (
              <p style={{
                textAlign: 'center',
                color: 'var(--ds-text-subtlest, #6B778C)',
                fontSize: '13px',
                padding: '16px'
              }}>
                No folders available
              </p>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: '16px 24px',
              borderTop: '1px solid var(--ds-border, #DFE1E6)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}
          >
            <Button
              appearance="subtle"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={selectedFolderId === currentFolderId}
            >
              Move Here
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export { MoveScenarioModal }
